import { ApplicationType } from '@wix/editor-application';

import {
  PlatformWorkerAPI,
  PlatformConsumerEnvironmentAPIType,
} from '@wix/editor-platform-environment-api';

import {
  WithPromiseFunctions,
  WorkerHostEndpoint,
} from '@wix/editor-platform-transport';

import {
  PlatformPrivateEventEmitter,
  IPlatformAppEvent,
} from '@wix/public-editor-platform-events';
import {
  createEditorPlatformWorkerError,
  EditorPlatformWorkerErrorCode,
} from '../../errors';
import { Essentials } from '../Essentials';
import { LimitedPrivateAPI, EnvType } from '@wix/editor-platform-private-api';

let callbackId = 0;

export abstract class PlatformWorkerStrategy {
  protected essentials: Essentials | undefined;
  private workerEventCallbacks: {
    id: number;
    fn: (event: IPlatformAppEvent) => void;
  }[] = [];
  public strategy: string;
  public state: Record<string, number> = {};

  protected constructor(
    protected limitedPrivateAPI: LimitedPrivateAPI,
    protected events: PlatformPrivateEventEmitter,
  ) {
    this.strategy = this.constructor.name;
  }

  abstract getWorkerAPI(): Promise<WithPromiseFunctions<PlatformWorkerAPI>>;

  abstract forEachWorker(
    cb: (workerAPI: WithPromiseFunctions<PlatformWorkerAPI>) => void,
  ): void;

  private updateWorkerState() {
    this.state[this.strategy] = (this.state[this.strategy] || 0) + 1;
  }

  private generateWorkerId() {
    return `${this.strategy}_${this.state[this.strategy]}`;
  }

  private buildPrivateAPI(config: { type: ApplicationType }) {
    return this.limitedPrivateAPI.build({
      env: EnvType.Worker,
      type: config.type,
    });
  }

  protected async spawnWorker(
    url: string,
  ): Promise<WithPromiseFunctions<PlatformWorkerAPI>> {
    this.updateWorkerState();

    const spawnWorkerEvent = this.events?.factories.createWorkerSpawnEvent({
      url,
      workerId: this.generateWorkerId(),
      strategy: this.strategy,
    });

    return this.events.withEvent(spawnWorkerEvent, async () => {
      try {
        const worker = new Worker(url);

        return new Promise((resolve, reject) => {
          WorkerHostEndpoint.connect(worker, async (transport) => {
            try {
              const workerApi = await transport.waitAPI<PlatformWorkerAPI>(
                PlatformConsumerEnvironmentAPIType.Worker,
              );

              await workerApi.initEnvironment({
                buildPrivateAPI: this.buildPrivateAPI.bind(this),
              });

              await workerApi.subscribe((event: IPlatformAppEvent) => {
                this.workerEventCallbacks.forEach(({ fn }) => {
                  fn(event);
                });
              });

              resolve(workerApi);
            } catch (e) {
              reject(e);
            }
          });
        });
      } catch (e: any) {
        throw createEditorPlatformWorkerError(
          EditorPlatformWorkerErrorCode.WorkerInitError,
        )
          .withParentError(e)
          .withUrl(url);
      }
    });
  }

  onWorkerEvent(cb: (event: IPlatformAppEvent) => void) {
    const _callbackId = ++callbackId;

    this.workerEventCallbacks.push({
      id: _callbackId,
      fn: cb,
    });

    return () => {
      this.workerEventCallbacks = this.workerEventCallbacks.filter(
        ({ id }) => id !== _callbackId,
      );
    };
  }
}
